import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { PaymentsService } from '../../core/services/payments.service';
import { SuppliersService } from '../../core/services/suppliers.service';
import { Payment, PaymentFilter, PaymentPagedResult } from '../../core/models/payment.model';
import { Supplier } from '../../core/models/supplier.model';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { CreatePaymentDialogComponent } from './create-payment-dialog.component';

@Component({
  selector: 'app-payments',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatSnackBarModule,
    DecimalPipe,
    DatePipe,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatTooltipModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatDialogModule
  ],
  template: `
    <div class="page-header">
      <div>
        <h1 class="page-title">Uplate</h1>
        <p class="page-subtitle">Pregled svih uplata dobavljačima</p>
      </div>
      <div class="header-actions">
        <button mat-raised-button color="primary" (click)="openCreateDialog()">
          <mat-icon>add</mat-icon>
          Nova uplata
        </button>
        <button mat-stroked-button (click)="goToPayables()">
          <mat-icon>receipt_long</mat-icon>
          Dugovanja
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-container">
      <div class="filters-row">
        <mat-form-field appearance="outline" class="filter-field">
          <mat-label>Dobavljač</mat-label>
          <mat-select [(ngModel)]="filter.supplierId" (selectionChange)="load()">
            <mat-option [value]="undefined">Svi dobavljači</mat-option>
            <mat-option *ngFor="let s of suppliers" [value]="s.id">{{ s.name }}</mat-option>
          </mat-select>
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-field">
          <mat-label>Od datuma</mat-label>
          <input matInput [matDatepicker]="fromPicker" [(ngModel)]="fromDate" (dateChange)="onDateChange()">
          <mat-datepicker-toggle matSuffix [for]="fromPicker"></mat-datepicker-toggle>
          <mat-datepicker #fromPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="outline" class="filter-field">
          <mat-label>Do datuma</mat-label>
          <input matInput [matDatepicker]="toPicker" [(ngModel)]="toDate" (dateChange)="onDateChange()">
          <mat-datepicker-toggle matSuffix [for]="toPicker"></mat-datepicker-toggle>
          <mat-datepicker #toPicker></mat-datepicker>
        </mat-form-field>

        <button mat-stroked-button (click)="clearFilters()" *ngIf="hasFilters()">
          <mat-icon>clear</mat-icon>
          Očisti filtere
        </button>
      </div>
    </div>

    <!-- Summary -->
    <div class="summary-container" *ngIf="!loading && data.items.length">
      <div class="summary-item">
        <span class="summary-label">Ukupno uplata:</span>
        <span class="summary-value">{{ data.totalCount }}</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">Ukupan iznos:</span>
        <span class="summary-value amount">{{ getTotalAmount() | number:'1.2-2' }} RSD</span>
      </div>
    </div>

    <!-- Loading -->
    <div class="loading-container" *ngIf="loading">
      <mat-spinner diameter="40"></mat-spinner>
    </div>

    <!-- Table -->
    <div class="table-container" *ngIf="!loading && data.items.length">
      <table mat-table [dataSource]="data.items" class="payments-table">
        <ng-container matColumnDef="supplierName">
          <th mat-header-cell *matHeaderCellDef>Dobavljač</th>
          <td mat-cell *matCellDef="let p">{{ p.supplierName }}</td>
        </ng-container>
        <ng-container matColumnDef="paymentDate">
          <th mat-header-cell *matHeaderCellDef>Datum uplate</th>
          <td mat-cell *matCellDef="let p">{{ p.paymentDate | date:'dd.MM.yyyy' }}</td>
        </ng-container>
        <ng-container matColumnDef="amount">
          <th mat-header-cell *matHeaderCellDef class="text-right">Iznos</th>
          <td mat-cell *matCellDef="let p" class="text-right amount-cell">{{ p.amount | number:'1.2-2' }}</td>
        </ng-container>
        <ng-container matColumnDef="allocatedAmount">
          <th mat-header-cell *matHeaderCellDef class="text-right">Raspoređeno</th>
          <td mat-cell *matCellDef="let p" class="text-right amount-cell allocated">{{ p.allocatedAmount | number:'1.2-2' }}</td>
        </ng-container>
        <ng-container matColumnDef="unallocatedAmount">
          <th mat-header-cell *matHeaderCellDef class="text-right">Neraspoređeno</th>
          <td mat-cell *matCellDef="let p" class="text-right amount-cell" [ngClass]="{'unallocated': p.unallocatedAmount > 0}">
            {{ p.unallocatedAmount | number:'1.2-2' }}
          </td>
        </ng-container>
        <ng-container matColumnDef="referenceNumber">
          <th mat-header-cell *matHeaderCellDef>Poziv na broj</th>
          <td mat-cell *matCellDef="let p">{{ p.referenceNumber || '-' }}</td>
        </ng-container>
        <ng-container matColumnDef="allocations">
          <th mat-header-cell *matHeaderCellDef class="text-center">Alokacije</th>
          <td mat-cell *matCellDef="let p" class="text-center">
            <span class="allocation-badge" [ngClass]="{'has-allocations': p.allocations?.length > 0}">
              {{ p.allocations?.length || 0 }}
            </span>
          </td>
        </ng-container>
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef class="table-actions">Akcije</th>
          <td mat-cell *matCellDef="let p" class="table-actions">
            <button mat-icon-button (click)="viewDetails(p)" matTooltip="Detalji" class="action-button">
              <mat-icon>visibility</mat-icon>
            </button>
            <button mat-icon-button (click)="confirmDelete(p)" matTooltip="Obriši" class="action-button delete-button">
              <mat-icon>delete</mat-icon>
            </button>
          </td>
        </ng-container>
        <tr mat-header-row *matHeaderRowDef="cols"></tr>
        <tr mat-row *matRowDef="let row; columns: cols;" class="table-row"></tr>
      </table>
      <mat-paginator
        [length]="data.totalCount"
        [pageSize]="filter.pageSize"
        [pageIndex]="(filter.page || 1) - 1"
        [pageSizeOptions]="[25, 50, 100]"
        (page)="onPage($event)"
        showFirstLastButtons>
      </mat-paginator>
    </div>

    <div class="empty-state" *ngIf="!loading && !data.items.length">
      <mat-icon>payments</mat-icon>
      <p>Nema pronađenih uplata</p>
      <button mat-stroked-button color="primary" (click)="goToPayables()">Idi na dugovanja</button>
    </div>
  `,
  styles: [`
    .page-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 24px;
    }

    .page-title {
      margin: 0;
      font-size: 28px;
      font-weight: 600;
      color: #1F2937;
    }

    .page-subtitle {
      margin: 4px 0 0 0;
      color: #6B7280;
      font-size: 14px;
    }

    .header-actions {
      display: flex;
      gap: 12px;
    }

    .filters-container {
      background: white;
      border-radius: 8px;
      padding: 16px 20px;
      margin-bottom: 16px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .filters-row {
      display: flex;
      align-items: center;
      gap: 16px;
      flex-wrap: wrap;
    }

    .filter-field {
      width: 200px;
    }

    .summary-container {
      display: flex;
      gap: 32px;
      margin-bottom: 16px;
      padding: 16px 20px;
      background: #EFF6FF;
      border-radius: 8px;
      border: 1px solid #BFDBFE;
    }

    .summary-item {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .summary-label {
      color: #1E40AF;
      font-size: 13px;
    }

    .summary-value {
      font-weight: 600;
      color: #1E3A8A;
    }

    .summary-value.amount {
      font-family: 'Roboto Mono', monospace;
    }

    .loading-container {
      display: flex;
      justify-content: center;
      padding: 60px;
    }

    .table-container {
      background: white;
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      overflow: hidden;
      border: 1px solid #E5E7EB;
    }

    .payments-table {
      width: 100%;
      background: white;
    }

    .payments-table th {
      background: #FFFFFF;
      color: #4B5563;
      font-weight: 600;
      padding: 12px 12px !important;
      border-bottom: 1px solid #D1D5DB;
      font-size: 13px;
    }

    .payments-table td {
      padding: 10px 12px !important;
      color: #1F2937;
      font-size: 13px;
      border-bottom: 1px solid #E5E7EB;
    }

    .text-right {
      text-align: right !important;
    }

    .text-center {
      text-align: center !important;
    }

    .amount-cell {
      font-family: 'Roboto Mono', monospace;
      font-weight: 500;
    }

    .amount-cell.allocated {
      color: #10B981;
    }

    .amount-cell.unallocated {
      color: #F59E0B;
      font-weight: 600;
    }

    .allocation-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 12px;
      font-weight: 600;
      background: #F3F4F6;
      color: #6B7280;
    }

    .allocation-badge.has-allocations {
      background: #D1FAE5;
      color: #059669;
    }

    .table-actions {
      text-align: right !important;
      width: 100px;
    }

    .action-button {
      transition: all 0.2s ease;
    }

    .action-button:hover {
      background-color: rgba(0, 0, 0, 0.06);
    }

    .action-button.delete-button:hover {
      color: #DC2626;
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 80px 20px;
      background: white;
      border-radius: 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
      border: 1px solid #E5E7EB;
    }

    .empty-state mat-icon {
      font-size: 64px;
      width: 64px;
      height: 64px;
      color: #D1D5DB;
      margin-bottom: 16px;
    }

    .empty-state p {
      color: #6B7280;
      font-size: 16px;
      margin: 0 0 16px 0;
    }
  `]
})
export class PaymentsComponent implements OnInit {
  private service = inject(PaymentsService);
  private suppliersService = inject(SuppliersService);
  private snack = inject(MatSnackBar);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  cols = ['supplierName', 'paymentDate', 'amount', 'allocatedAmount', 'unallocatedAmount', 'referenceNumber', 'allocations', 'actions'];
  data: PaymentPagedResult = { items: [], totalCount: 0, page: 1, pageSize: 50, totalPages: 0 };
  suppliers: Supplier[] = [];
  loading = false;

  filter: PaymentFilter = {
    page: 1,
    pageSize: 50
  };

  fromDate: Date | null = null;
  toDate: Date | null = null;

  ngOnInit(): void {
    this.suppliersService.getAll().subscribe({
      next: (list) => {
        this.suppliers = list;
        this.load();
      },
      error: () => this.load()
    });
  }

  load() {
    this.loading = true;
    this.service.getFiltered(this.filter).subscribe({
      next: (result) => {
        this.data = result;
        this.loading = false;
      },
      error: () => {
        this.snack.open('Greška pri učitavanju podataka', 'Zatvori', { duration: 3000 });
        this.loading = false;
      }
    });
  }

  onPage(event: PageEvent) {
    this.filter.page = event.pageIndex + 1;
    this.filter.pageSize = event.pageSize;
    this.load();
  }

  onDateChange() {
    this.filter.fromDate = this.fromDate ? this.fromDate.toISOString() : undefined;
    this.filter.toDate = this.toDate ? this.toDate.toISOString() : undefined;
    this.load();
  }

  hasFilters(): boolean {
    return !!this.filter.supplierId || !!this.fromDate || !!this.toDate;
  }

  clearFilters() {
    this.filter.supplierId = undefined;
    this.fromDate = null;
    this.toDate = null;
    this.filter.fromDate = undefined;
    this.filter.toDate = undefined;
    this.load();
  }

  getTotalAmount(): number {
    return this.data.items.reduce((sum, p) => sum + p.amount, 0);
  }

  viewDetails(payment: Payment) {
    // Navigate to payables filtered by supplier to see related documents
    this.router.navigate(['/payables'], { queryParams: { supplierId: payment.supplierId } });
  }

  confirmDelete(payment: Payment) {
    const yes = confirm(`Da li ste sigurni da želite da obrišete ovu uplatu? Ovo će poništiti alokacije na povezanim računima.`);
    if (!yes) return;
    this.service.delete(payment.id).subscribe({
      next: () => {
        this.snack.open('Uplata je obrisana', 'Zatvori', { duration: 2000 });
        this.load();
      },
      error: (e) => this.snack.open(e?.error?.message || 'Greška pri brisanju', 'Zatvori', { duration: 3000 })
    });
  }

  goToPayables() {
    this.router.navigate(['/payables']);
  }

  openCreateDialog() {
    const dialogRef = this.dialog.open(CreatePaymentDialogComponent, {
      width: '650px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.snack.open('Uplata je uspešno kreirana', 'Zatvori', { duration: 3000 });
        this.load();
      }
    });
  }
}
